// PostHog — session replay, console/network capture, usage analytics.
// Replays are viewable in the PostHog dashboard; the bug reporter attaches a
// replay link to every report so devs can watch exactly what happened.
//
// **Nothing below production loads PostHog at all.** Replays carry typed input and
// console output, so a develop or staging bundle must not record into a project
// that production's staff data is read from. The gate is the declared environment
// (`portalEnv.js`), never the hostname, and an unset or unrecognised value is not
// production — so it fails towards loading nothing. Both initialisations sit behind
// it: the vendor loader snippet (`posthogSnippet.js`) and the npm client below.
import posthog from "posthog-js";
import { isProduction } from "./portalEnv";
import { installPostHogSnippet } from "./posthogSnippet";

/**
 * The build-time variables this bundle was compiled with.
 *
 * Every `process.env.REACT_APP_*` below must stay a literal reference or CRA will
 * not inline it and the value reaches the browser as `undefined`.
 *
 * @returns {{portalEnv: string|undefined, key: string|undefined, host: string|undefined}}
 */
const buildTimeVars = () => ({
  portalEnv: process.env.REACT_APP_PORTAL_ENV,
  key: process.env.REACT_APP_POSTHOG_KEY,
  host: process.env.REACT_APP_POSTHOG_HOST,
});

/**
 * Whether this bundle may load PostHog at all: production only.
 *
 * @param {{portalEnv?: string}} [vars] build-time variables; defaults to this bundle's own.
 * @returns {boolean}
 */
export const posthogPermitted = (vars = buildTimeVars()) => isProduction(vars?.portalEnv ?? "");

const SENSITIVE = /password|sort.?code|account.?number|iban|secret|token|card/i;

/**
 * Start PostHog: the vendor snippet first, then the npm client when a key and host
 * were supplied at build time — the order and the arguments production has always
 * had.
 *
 * The returned status exists as a test seam (a skipped call and a failed one are
 * otherwise indistinguishable, so deleting the gate would not turn anything red);
 * call sites ignore it.
 *
 * @param {{portalEnv?: string, key?: string, host?: string}} [vars]
 * @returns {"skipped"|"snippet"|"snippet+client"}
 */
export const initPostHog = (vars = buildTimeVars()) => {
  if (!posthogPermitted(vars)) return "skipped";
  installPostHogSnippet();
  const { key, host } = vars;
  if (!key || !host) return "snippet";
  posthog.init(key, {
    api_host: host,
    defaults: "2025-05-24", // SPA pageviews on history change
    capture_pageleave: true,
    capture_performance: true, // network timings in replays
    capture_exceptions: true, // uncaught JS errors -> PostHog error tracking
    enable_recording_console_log: true,
    person_profiles: "identified_only",
    session_recording: {
      maskAllInputs: false, // internal staff tool — keep inputs visible for debugging
      maskInputOptions: { password: true },
      maskInputFn: (text, element) => {
        const hint = `${element?.name || ""} ${element?.id || ""} ${element?.placeholder || ""} ${
          element?.getAttribute?.("data-testid") || ""
        }`;
        return SENSITIVE.test(hint) ? "*".repeat(text.length) : text;
      },
    },
  });
  return "snippet+client";
};

/**
 * Whether the npm client was started, so the calls below have something to talk to — the same
 * condition `initPostHog` applies before it starts the client.
 */
const clientActive = (vars = buildTimeVars()) => posthogPermitted(vars) && !!vars.key && !!vars.host;

// Link events + replays to the logged-in portal user
export const identifyPostHogUser = (user) => {
  if (!clientActive() || !user?.email) return;
  posthog.identify(user.email, {
    email: user.email,
    name: user.name,
    role: user.role,
    team: user.team,
    is_dev_bypass: !!user.is_dev_bypass,
  });
};

export const resetPostHog = () => {
  if (!clientActive()) return;
  posthog.reset();
};

// Replay URL for the current session (attached to bug reports)
export const getSessionReplayUrl = () => {
  if (!clientActive()) return null;
  try {
    return posthog.get_session_replay_url({ withTimestamp: true, timestampLookBack: 30 }) || null;
  } catch {
    return null;
  }
};
